import { useSyncExternalStore } from 'react';
import { openLocal, type LocalDb } from './localdb';
import { supabase } from '../lib/supabase';
import { aoComprar, comprasLocais, iniciarCompras, lojaDisponivel, sairCompras } from '../lib/compras';
import { DOC_IDS, MOD_IDS, VAZIO, type DocId, type Docs } from './types';
import { juntar } from './mesclar';

// ─── Armazenamento local (primeiro no aparelho, depois na nuvem) ───────────────────────────────
// Cada aba é um documento JSON. Toda alteração grava no aparelho na hora e marca o documento
// como "pendente"; a sincronização envia os pendentes e baixa o que mudou em outro aparelho.

const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
type Meta = { versao: number; dirty: boolean; rev: number };
let state: Docs = clone(VAZIO);
const meta: Record<string, Meta> = {};
let userId: string | null = null;
let db: LocalDb | null = null;
const listeners = new Set<() => void>();
const changeHooks = new Set<(id: DocId) => void>();

export type SyncState = { status: 'idle' | 'syncing' | 'ok' | 'error' | 'offline' | 'sessao'; last: string | null; error?: string };
let sync: SyncState = { status: 'idle', last: null };
const syncListeners = new Set<() => void>();
const setSync = (s: Partial<SyncState>) => { sync = { ...sync, ...s }; syncListeners.forEach(l => l()); };

const emit = () => listeners.forEach(l => l());

// ─── Liberações por CPF (definidas por você no Supabase, tabela "liberacoes") ─────────────────
export type Liberacao = { abas: string[]; calendarios: string[]; creditos: number; validade: string | null; observacao: string | null; ilimitado: boolean;
  loja?: Record<string, string>;     // abas assinadas pela Google Play / App Store → vence em (0.64)
  teste?: boolean };                 // 0.72 (texto 10 C10): conta de teste de compras (tabela contas_teste): cobrança ligada e nenhuma liberação
const SEM_LIB: Liberacao = { abas: [], calendarios: [], creditos: 0, validade: null, observacao: null, ilimitado: false, loja: {} };
/** Todas as abas pagas (para quem tem acesso ilimitado). */
export const TODAS_ABAS: string[] = ['casa', 'estudos', 'bemestar', 'financas', 'garantias', 'documentos', 'monitor', ...MOD_IDS];
let lib: Liberacao = SEM_LIB;
const libListeners = new Set<() => void>();
function setLib(l: Liberacao) { lib = l; libListeners.forEach(f => f()); }
export const getLiberacao = () => lib;
export function useLiberacao() {
  return useSyncExternalStore(cb => { libListeners.add(cb); return () => libListeners.delete(cb); }, () => lib);
}
/** Abas liberadas pelo administrador, respeitando a validade. */
export function abasLiberadas(l: Liberacao = lib) {
  if (l.ilimitado) return TODAS_ABAS;
  if (l.validade && l.validade < new Date().toISOString().slice(0, 10)) return [];
  return l.abas;
}

/** Completa campos que não existiam em versões antigas do documento. */
function comPadrao<K extends DocId>(id: K, dados: unknown): Docs[K] {
  const base = clone(VAZIO[id]) as any;
  if (!dados || typeof dados !== 'object') return base;
  const r = { ...base, ...(dados as object) };
  if (id === 'ajustes') { r.energia = { ...base.energia, ...(r.energia ?? {}) }; r.resumo = { ...base.resumo, ...(r.resumo ?? {}) }; }
  return r;
}

async function abrir() {
  if (db) return db;
  db = await openLocal();
  return db;
}

/** Carrega os dados do usuário. Se outro usuário entrar neste aparelho, os dados locais anteriores são apagados. */
export async function initStore(uid: string) {
  const d = await abrir();
  const dono = await d.getFirstAsync<{ value: string }>("SELECT value FROM meta WHERE key = 'owner'");
  if (dono?.value !== uid) {
    await d.execAsync("DELETE FROM docs; DELETE FROM meta WHERE key IN ('liberacao', 'sinc');");
    await d.runAsync("INSERT OR REPLACE INTO meta (key, value) VALUES ('owner', ?)", [uid]);
  }
  try {
    const l = await d.getFirstAsync<{ value: string }>("SELECT value FROM meta WHERE key = 'liberacao'");
    setLib(l ? { ...SEM_LIB, ...JSON.parse(l.value) } : SEM_LIB);
  } catch { setLib(SEM_LIB); }
  userId = uid;
  try { const x = await d.getFirstAsync<{ value: string }>("SELECT value FROM meta WHERE key = 'sinc'"); sinc = x ? JSON.parse(x.value) : {}; } catch { sinc = {}; }
  const rows = await d.getAllAsync<{ id: string; dados: string; versao: number; dirty: number }>('SELECT * FROM docs');
  const novo = clone(VAZIO) as any;
  for (const id of DOC_IDS) meta[id] = { versao: 0, dirty: false, rev: 0 };
  for (const r of rows) {
    if (!(DOC_IDS as string[]).includes(r.id)) continue;
    try { novo[r.id] = comPadrao(r.id as DocId, JSON.parse(r.dados)); } catch { /* documento corrompido: fica o padrão */ }
    meta[r.id] = { versao: r.versao, dirty: !!r.dirty, rev: 0 };
  }
  state = novo;
  emit();
  syncNow().catch(() => {});
}

export async function clearStore() {
  const d = await abrir();
  await d.execAsync("DELETE FROM docs; DELETE FROM meta;");
  userId = null; sinc = {};
  state = clone(VAZIO);
  setLib(SEM_LIB);
  lojaServidor = {}; sairCompras().catch(() => {});
  emit();
  setSync({ status: 'idle', last: null, error: undefined });
}

async function gravarLocal(id: DocId) {
  const d = await abrir();
  const m = meta[id];
  await d.runAsync('INSERT OR REPLACE INTO docs (id, dados, versao, dirty) VALUES (?,?,?,?)',
    [id, JSON.stringify(state[id]), m.versao, m.dirty ? 1 : 0]);
}

// ─── Leitura e escrita ─────────────────────────────────────────────────────────────────────────
export const getDoc = <K extends DocId>(id: K): Docs[K] => state[id];

export function setDoc<K extends DocId>(id: K, fn: (d: Docs[K]) => Docs[K]) {
  state = { ...state, [id]: fn(state[id]) };
  const m = meta[id] ?? (meta[id] = { versao: 0, dirty: false, rev: 0 });
  m.dirty = true; m.rev++;
  emit();
  gravarLocal(id).catch(() => {});
  changeHooks.forEach(h => h(id));
  agendarSync();
}

/** Substitui tudo (restauração de backup). */
export function replaceAll(dados: Partial<Docs>) {
  for (const id of DOC_IDS) if (dados[id]) setDoc(id, () => comPadrao(id, dados[id]) as any);
}

export function useDoc<K extends DocId>(id: K): Docs[K] {
  return useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb); }, () => state[id]);
}
export function onDocChange(fn: (id: DocId) => void) { changeHooks.add(fn); return () => { changeHooks.delete(fn); }; }
export function useSync() {
  return useSyncExternalStore(cb => { syncListeners.add(cb); return () => syncListeners.delete(cb); }, () => sync);
}
export const allDocs = () => state;

// ─── Sincronização com o Supabase ──────────────────────────────────────────────────────────────
let timer: ReturnType<typeof setTimeout> | null = null;
function agendarSync() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; syncNow().catch(() => {}); }, 2500);
}

/** Quando cada aba ficou igual ao servidor pela última vez (para juntar versões sem trazer de volta o que foi apagado). */
let sinc: Record<string, string> = {};
function marcarSinc(id: DocId) {
  sinc[id] = new Date().toISOString();
  abrir().then(d => d.runAsync("INSERT OR REPLACE INTO meta (key, value) VALUES ('sinc', ?)", [JSON.stringify(sinc)])).catch(() => {});
}
/** Há alterações deste dispositivo que ainda não foram para o servidor? */
export const temPendente = () => !!userId && userId !== 'demo' && DOC_IDS.some(id => meta[id]?.dirty);

let rodando: Promise<void> | null = null;
let deNovo = false;
/** Sincroniza agora. Se já estiver sincronizando, roda mais uma vez ao terminar (para não perder alterações). */
export function syncNow(): Promise<void> {
  if (rodando) { deNovo = true; return rodando.then(() => (deNovo ? syncNow() : (rodando ?? undefined))); }
  deNovo = false;
  rodando = executarSync().finally(() => { rodando = null; });
  return rodando;
}

/** Confere se a sessão do servidor ainda é deste usuário (renova se precisar). Sem sessão válida o servidor recusa tudo. */
async function sessaoOk(forcar = false): Promise<'ok' | 'sem' | 'offline'> {
  if (!supabase || !userId) return 'sem';
  try {
    if (!forcar) {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user.id === userId && (data.session.expires_at ?? 0) * 1000 > Date.now()) return 'ok';
    }
    const { data, error } = await supabase.auth.refreshSession();
    if (error && /fetch|network|timeout|Retryable/i.test(`${error.name} ${error.message}`)) return 'offline';
    return data.session?.user.id === userId ? 'ok' : 'sem';
  } catch { return 'offline'; }
}
const ehRls = (e: any) => e?.code === '42501' || /row-level security|JWT|not authorized|permission denied/i.test(String(e?.message ?? e));

async function executarSync(tentativa = 0): Promise<void> {
  if (!supabase || !userId || userId === 'demo') return;
  setSync({ status: 'syncing' });
  const ses = await sessaoOk();
  if (ses === 'offline') { setSync({ status: 'offline' }); setTimeout(() => syncNow().catch(() => {}), 60000); return; }
  if (ses === 'sem') { setSync({ status: 'sessao', error: undefined }); return; }
  try {
    // 1. Envia o que mudou aqui. Se outro dispositivo enviou antes (versão do servidor maior do que a que este
    //    dispositivo conhece), junta as duas versões em vez de apagar o que o outro fez (0.77).
    for (const id of DOC_IDS) {
      const m = meta[id];
      if (!m?.dirty) continue;
      for (let volta = 0; volta < 4; volta++) {
        const { data: remoto, error: e1 } = await supabase.from('planner_dados').select('versao').eq('aba', id).maybeSingle();
        if (e1) throw e1;
        const rv = remoto?.versao ?? 0;
        if (rv > m.versao) {
          const { data: doc, error: e4 } = await supabase.from('planner_dados').select('dados, versao').eq('aba', id).maybeSingle();
          if (e4) throw e4;
          if (doc && doc.versao > m.versao) {
            state = { ...state, [id]: comPadrao(id, juntar(state[id], comPadrao(id, doc.dados), sinc[id] ?? '')) };
            m.versao = doc.versao; m.rev++;
            await gravarLocal(id);
            changeHooks.forEach(h => h(id));
            emit();
          }
          continue;   // confere de novo antes de enviar
        }
        const rev = m.rev;
        const versao = Math.max(m.versao, rv) + 1;
        const linha = { user_id: userId!, aba: id, dados: state[id], versao, atualizado_em: new Date().toISOString() };
        let ok = true;
        if (remoto) {
          // Só grava se ninguém enviou no meio do caminho (versão ainda é a mesma).
          const { data: up, error } = await supabase.from('planner_dados').update(linha).eq('user_id', userId!).eq('aba', id).eq('versao', rv).select('versao');
          if (error) throw error;
          ok = (up ?? []).length > 0;
        } else {
          const { error } = await supabase.from('planner_dados').upsert(linha, { onConflict: 'user_id,aba' });
          if (error) throw error;
        }
        if (!ok) continue;
        m.versao = versao;
        if (m.rev === rev) m.dirty = false;  // se mudou durante o envio, continua pendente
        marcarSinc(id);
        await gravarLocal(id);
        break;
      }
    }
    // 2. Baixa o que mudou em outro aparelho.
    const { data: lista, error: e2 } = await supabase.from('planner_dados').select('aba, versao');
    if (e2) throw e2;
    for (const r of lista ?? []) if (!sinc[r.aba] && meta[r.aba] && !meta[r.aba].dirty && r.versao === meta[r.aba].versao) marcarSinc(r.aba as DocId);
    const baixar = (lista ?? []).filter(r => (DOC_IDS as string[]).includes(r.aba) && !meta[r.aba]?.dirty && r.versao > (meta[r.aba]?.versao ?? 0));
    if (baixar.length) {
      const { data: docs, error: e3 } = await supabase.from('planner_dados').select('aba, dados, versao').in('aba', baixar.map(b => b.aba));
      if (e3) throw e3;
      for (const r of docs ?? []) {
        const id = r.aba as DocId;
        if (meta[id]?.dirty) continue;
        state = { ...state, [id]: comPadrao(id, r.dados) };
        meta[id] = { versao: r.versao, dirty: false, rev: meta[id]?.rev ?? 0 };
        marcarSinc(id);
        await gravarLocal(id);
        changeHooks.forEach(h => h(id));
      }
      emit();
    }
    await buscarLiberacao();
    if (DOC_IDS.some(id => meta[id]?.dirty)) agendarSync();
    setSync({ status: 'ok', last: new Date().toISOString(), error: undefined });
  } catch (e: any) {
    // Sessão vencida ou trocada: renova e tenta mais uma vez; se não der, pede para entrar de novo (nada se perde).
    if (ehRls(e)) {
      if (tentativa === 0 && (await sessaoOk(true)) === 'ok') return executarSync(1);
      setSync({ status: 'sessao', error: undefined });
      return;
    }
    const msg = String(e?.message ?? e);
    const offline = /network|fetch|timeout/i.test(msg);
    setSync({ status: offline ? 'offline' : 'error', error: msg });
    if (offline) setTimeout(() => syncNow().catch(() => {}), 60000);
  }
}

let lojaServidor: Record<string, string> = {};
/** Liga a Google Play / App Store para o usuário que entrou (0.64). Compra nova libera a aba na hora. */
async function ligarLoja() {
  if (!supabase || !lojaDisponivel() || !userId) return;
  aoComprar(() => { conferirCompras().catch(() => {}); });
  await iniciarCompras(userId, m => setLib({ ...lib, loja: { ...lojaServidor, ...m } }));
}
/** Pede ao servidor para conferir as assinaturas agora (o aviso da loja pode demorar alguns segundos). */
export async function conferirCompras() {
  if (!supabase) return;
  await supabase.functions.invoke('compras', { body: { conferir: true } }).catch(() => {});
  await buscarLiberacao();
}

/** Lê a liberação do próprio CPF. Se a tabela ainda não existir, segue sem liberações. */
async function buscarLiberacao() {
  if (!supabase) return;
  const { data, error } = await supabase.from('liberacoes').select('*').maybeSingle();
  if (error) return;
  // Assinaturas da loja (0.64). Se a tabela ainda não existir, segue sem elas.
  const { data: ass } = await supabase.from('assinaturas_loja').select('aba, expira');
  lojaServidor = Object.fromEntries((ass ?? []).filter((x: any) => x.aba).map((x: any) => [x.aba, x.expira ?? '9999-12-31']));
  // 0.72 (texto 10 C10): conta de teste de compras? Então nenhuma liberação vale (nem a do CPF) e a cobrança fica ligada.
  const { data: tst } = await supabase.from('contas_teste').select('user_id').maybeSingle();
  const l: Liberacao = tst ? { ...SEM_LIB, teste: true } : data ? {
    abas: data.abas ?? [], calendarios: data.calendarios ?? [], creditos: data.creditos ?? 0,
    validade: data.validade ?? null, observacao: data.observacao ?? null, ilimitado: !!data.ilimitado,
  } : { ...SEM_LIB };
  l.loja = { ...lojaServidor, ...comprasLocais() };
  setLib(l);
  ligarLoja();
  try { await (await abrir()).runAsync("INSERT OR REPLACE INTO meta (key, value) VALUES ('liberacao', ?)", [JSON.stringify(l)]); } catch { /* ignora */ }
}
/** Todos os documentos (re-renderiza em qualquer mudança). Usar só em painéis que juntam várias abas. */
export function useAllDocs(): Docs {
  return useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb); }, () => state);
}
